import { useSettings } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { PageHero, Reveal, SectionHead } from "@/components/Motion";
import { ShieldCheck } from "lucide-react";

export default function Quality() {
  const { settings } = useSettings();
  if (!settings) return <div className="min-h-screen bg-parchment" />;
  const seo = settings.seo?.pages?.quality;

  return (
    <div data-testid="quality-page">
      <SEO title={seo?.title} description={seo?.description} path="/quality" />
      <PageHero
        eyebrow="Quality & Certifications"
        title="Quality, checked at every step."
        description="Our quality approach covers supplier selection through to pre-shipment checks and export documentation — so buyers receive what was agreed."
        image={settings.categories?.rice?.image}
      />

      <section className="bg-parchment py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <SectionHead eyebrow="Our Quality Process" title="From supplier to shipment." />
          <div className="mt-14 grid gap-px bg-forest/10 sm:grid-cols-2 lg:grid-cols-3">
            {(settings.quality_steps || []).map((step, i) => (
              <Reveal key={step.title} delay={(i % 3) * 0.08}>
                <div className="h-full bg-white p-8" data-testid={`quality-step-${i + 1}`}>
                  <span className="font-serif italic text-3xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-serif text-2xl text-forest">{step.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#4A554D]">{step.description}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="h-full bg-forest p-8 grain relative flex flex-col justify-center">
                <ShieldCheck className="h-8 w-8 text-gold" />
                <p className="mt-4 font-serif text-2xl text-parchment leading-snug">Quality parameters are agreed with each buyer before order confirmation.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-forest py-20 lg:py-24 grain relative" data-testid="certifications-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <SectionHead dark eyebrow="Certifications" title="Certified for global trade." description="Certification documentation is shared with buyers during the quotation process." />
          {(settings.certifications || []).length === 0 ? (
            <div className="mt-12 flex h-36 items-center justify-center border border-dashed border-gold/40 bg-forest-mid/40" data-testid="certifications-empty">
              <span className="font-mono text-xs uppercase tracking-[0.24em] text-gold/70">Certification details available on request</span>
            </div>
          ) : (
            <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="certifications-grid">
              {settings.certifications.map((c, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1}>
                  <div className="group flex h-full gap-5 bg-forest-deep/70 p-7 transition-colors duration-500 hover:bg-forest-mid/70" data-testid={`certification-${i + 1}`}>
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center border border-gold/30 bg-parchment p-2">
                      {c.logo ? (
                        <img src={c.logo} alt={`${c.name} logo`} loading="lazy" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <ShieldCheck className="h-8 w-8 text-gold-dark" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-serif text-xl text-parchment leading-snug">{c.name}</h3>
                      {c.description && <p className="mt-2 text-sm leading-relaxed text-[#9EB8A8]">{c.description}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
